(function () {
  'use strict';

  var CONFIG_ELEMENT_ID = 'predictive-bot-config';
  var LOG_PREFIX = '[predictive-bot]';
  var READING_LINE = 0.45;
  var BAND_HEIGHT = 0.02;
  var RANK_EPSILON_PX = 1;
  var MOBILE_MAX_WIDTH = 767;
  var NUDGE_EVENT = 'predictive-bot:nudge';
  var AB_ENABLE_FLAG = 'enable_predictive_bot';
  var AB_DEBUG_FLAG = 'pb_debug_logs';
  var LOG_ENDPOINT = 'https://api-v1.mamaearth.in/v1/logs/batch';
  var LOG_THREAD = 'main';
  var DEVICE_ID_KEY = 'shopifyDeviceId';
  var LOG_FLUSH_MS = 5000;
  var LOG_BATCH_MAX = 20;
  var LOG_QUEUE_MAX = 200;
  var LOG_MAX_CHARS = 2000;

  if (window.__predictiveBotStarted) return;
  window.__predictiveBotStarted = true;


  // =========================================================================
  //  DEBUG FLAGS
  // =========================================================================

  // Browser-log switch, read fresh so it can be flipped from the console.
  function isDebug() {
    return window.pb_debug_logs === true;
  }

  // AB flag gating info logs to the server; no Shopify object means off.
  function isServerInfoEnabled() {
    return abFlag(AB_DEBUG_FLAG);
  }

  // One CDP experiment flag; only an explicit true enables.
  function abFlag(name) {
    var shopify = window.Shopify;
    var experiments = shopify && shopify.abExperimentData;
    var flag = experiments && experiments[name];
    return flag === true || flag === 'true';
  }


  // =========================================================================
  //  LOGGING
  // =========================================================================

  var logQueue = [];
  var logFlushTimer = null;
  var logDeviceId = null;
  var logFailureReported = false;

  // Device id shared with the rest of the theme, so logs join a shopper's other calls.
  function deviceId() {
    try {
      return window.localStorage.getItem(DEVICE_ID_KEY);
    } catch (error) {
      warn('error_device_id', error);
      return null;
    }
  }

  // Info level: console when debugging, server when the AB flag is on.
  function log() {
    if (isDebug()) {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(LOG_PREFIX);
      console.log.apply(console, args);
    }
    if (isServerInfoEnabled()) queueServerLog('info', arguments);
  }

  // Error level: console when debugging, always sent to the server.
  function warn() {
    if (isDebug()) {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(LOG_PREFIX);
      console.warn.apply(console, args);
    }
    queueServerLog('error', arguments);
  }

  // Wraps a callback so a throw is reported instead of escaping into the browser.
  function guard(event, fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (error) {
        warn(event, error);
        return undefined;
      }
    };
  }

  // log format
  function toEntry(level, args) {
    var rest = Array.prototype.slice.call(args, 1);
    var detail = serialise(rest);

    return {
      level: level,
      message: args[0] + (detail ? ' ' + detail : ''),
      thread: LOG_THREAD,
      url: window.location.href,
      log_timestamp: new Date().toISOString()
    };
  }

  // Flattens log arguments into one capped string; never throws.
  function serialise(args) {
    var parts = [];

    for (var i = 0; i < args.length; i++) {
      var value = args[i];

      if (typeof value === 'string') {
        parts.push(value);
      } else if (value && typeof value.message === 'string') {
        parts.push(value.message || String(value));
      } else {
        try {
          parts.push(JSON.stringify(value));
        } catch (error) {
          parts.push(String(value));
        }
      }
    }

    var text = parts.join(' ');
    return text.length > LOG_MAX_CHARS ? text.slice(0, LOG_MAX_CHARS) + '…' : text;
  }

  // Buffers a log line; errors and full batches flush immediately.
  function queueServerLog(level, args) {
    logQueue.push(toEntry(level, args));
    if (logQueue.length > LOG_QUEUE_MAX) logQueue.splice(0, logQueue.length - LOG_QUEUE_MAX);

    if (level === 'error' || logQueue.length >= LOG_BATCH_MAX) {
      flushLogs();
      return;
    }
    if (!logFlushTimer) logFlushTimer = setTimeout(flushLogs, LOG_FLUSH_MS);
  }

  // POSTs the buffered batch and empties the queue.
  function flushLogs() {
    if (logFlushTimer) {
      clearTimeout(logFlushTimer);
      logFlushTimer = null;
    }
    if (!logQueue.length) return;

    var batch = logQueue.slice();
    logQueue.length = 0;

    try {
      fetch(LOG_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-source': 'web-app',
          'x-platform': 'web',
          'x-device-id': logDeviceId || ''
        },
        body: JSON.stringify({ logs: batch }),
        keepalive: true
      })
        .then(function (response) {
          if (response && response.ok) {
            logFailureReported = false;
            return;
          }
          reportFlushFailure('status ' + ((response && response.status) || 'unknown'));
        })
        ['catch'](function (error) {
          reportFlushFailure((error && error.message) || 'network error');
        });
    } catch (error) {
      reportFlushFailure((error && error.message) || 'fetch unavailable');
    }
  }

  // Records a failed flush without triggering one
  function reportFlushFailure(detail) {
    if (isDebug()) console.warn(LOG_PREFIX, 'error_log_flush', detail);
    if (logFailureReported) return;

    logFailureReported = true;
    logQueue.push(toEntry('error', ['error_log_flush', detail]));
  }


  // =========================================================================
  //  WORKER MESSAGING
  // =========================================================================

  // Sends a message to the worker, stamping both live debug flags.
  function post(worker, type, payload) {
    worker.postMessage({
      type: type,
      payload: payload,
      debug: isDebug(),
      serverInfo: isServerInfoEnabled()
    });
  }

  // Announces a nudge to the page; the nudge UI listens for this event.
  function dispatchNudge(payload, tracker) {
    var nudge = payload || {};

    try {
      document.dispatchEvent(
        new CustomEvent(NUDGE_EVENT, {
          bubbles: true,
          detail: {
            nudgeText: nudge.nudgeText || '',
            chatPrefillText: nudge.chatPrefillText || '',
            sectionId: (tracker && tracker.sectionId(nudge.sectionKey)) || null
          }
        })
      );
      log('nudge_dispatch', {nudgeText: nudge.nudgeText || '', chatPrefillText: nudge.chatPrefillText || '', sectionId: (tracker && tracker.sectionId(nudge.sectionKey)) || null});
    } catch (error) {
      warn('error_nudge_dispatch', error);
    }
  }

  // =========================================================================
  //  SCHEDULING
  // =========================================================================

  // Defers work to idle time so boot never competes with render.
  function onIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout: 3000 });
    } else {
      setTimeout(callback, 200);
    }
  }


  // =========================================================================
  //  STARTUP
  // =========================================================================

  // Parses the Liquid-rendered config JSON tag.
  function readConfig() {
    var element = document.getElementById(CONFIG_ELEMENT_ID);
    if (!element) {
      warn('config_missing', 'element #' + CONFIG_ELEMENT_ID + ' is not in the DOM');
      return null;
    }

    try {
      return JSON.parse(element.textContent || element.innerHTML);
    } catch (error) {
      warn('config_unparsable', error);
      return null;
    }
  }

  // Starts the worker from the theme asset URL.
  function createWorker(url) {
    if (!url) {
      warn('worker_url_missing', 'no workerUrl in config');
      return null;
    }

    try {
      return new Worker(url);
    } catch (error) {
      warn('worker_start_failed', url, error);
      return null;
    }
  }


  // =========================================================================
  //  DATALAYER
  // =========================================================================

  // Wraps dataLayer.push to forward the configured event names.
  function watchDataLayer(worker, names) {
    if (!names || !names.length) return;

    try {
      var wanted = {};
      for (var n = 0; n < names.length; n++) wanted[names[n]] = true;

      var layer = (window.dataLayer = window.dataLayer || []);

      // Forwards one dataLayer entry if its event name is wanted.
      var mirror = guard('error_datalayer_mirror', function (entry) {
        if (!entry) return;

        var name = entry.event;
        if (typeof name !== 'string' || !wanted[name]) return;

        log('datalayer_event', name);
        post(worker, 'datalayer-event', { name: name, timestamp: Date.now() });
      });

      for (var i = 0; i < layer.length; i++) mirror(layer[i]);

      var push = layer.push;
      layer.push = function () {
        var result = push.apply(this, arguments);

        for (var a = 0; a < arguments.length; a++) mirror(arguments[a]);

        return result;
      };
    } catch (error) {
      warn('error_datalayer_watch', error);
    }
  }


  // =========================================================================
  //  VISIBILITY TRACKING
  // =========================================================================

  // Negative root margin shrinking the viewport to just the reading band.
  function bandRootMargin() {
    var top = +((READING_LINE - BAND_HEIGHT / 2) * 100).toFixed(4);
    var bottom = +(100 - (READING_LINE + BAND_HEIGHT / 2) * 100).toFixed(4);
    return '-' + top + '% 0px -' + bottom + '% 0px';
  }

  // Section id -> section_position from the shop config, for tie-breaking.
  function buildPriority(shopConfig) {
    var priority = {};

    try {
      var list = (shopConfig && shopConfig.sections_list) || [];

      for (var i = 0; i < list.length; i++) {
        var rule = list[i];
        if (!rule || !rule.section_id) continue;
        if (typeof rule.section_position !== 'number' || !isFinite(rule.section_position)) continue;
        priority[rule.section_id] = rule.section_position;
      }
    } catch (error) {
      warn('error_build_priority', error);
    }

    return priority;
  }

  // Reports whichever configured section currently sits under the reading line.
  function createTracker(worker, config) {
    var sections = config.sections;
    var priority = buildPriority(config.shopConfig);
    var targets = [];
    var missing = [];
    var byElement = new Map();
    var observer = null;
    var active = null;
    var lastEmitted = null;
    var suspended = false;

    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      if (!section || !section.id) continue;

      var element = document.getElementById(section.id);
      if (!element) {
        missing.push(section.id);
        continue;
      }

      targets.push({
        id: section.id,
        key: section.key || null,
        element: element,
        inBand: false
      });
    }

    if (missing.length) {
      warn('sections_missing', missing.join(','));
    }
    if (!targets.length) return null;

    try {
      targets.sort(function (a, b) {
        var pa = priority[a.key];
        var pb = priority[b.key];
        if (typeof pa === 'number' && typeof pb === 'number' && pa !== pb) return pa - pb;

        var position = a.element.compareDocumentPosition(b.element);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
    } catch (error) {
      warn('error_sort_targets', error);
    }

    for (var t = 0; t < targets.length; t++) {
      byElement.set(targets[t].element, targets[t]);
    }

    // One observer whose root is the reading band, so intersecting means "being read".
    function observe() {
      try {
        observer = new IntersectionObserver(guard('error_observer', handleEntries), {
          rootMargin: bandRootMargin(),
          threshold: 0
        });

        for (var i = 0; i < targets.length; i++) {
          observer.observe(targets[i].element);
        }
      } catch (error) {
        warn('error_observe', error);
      }
    }

    // Records which sections entered or left the band, then re-picks the active one.
    function handleEntries(entries) {
      var timestamp = Date.now();

      for (var i = 0; i < entries.length; i++) {
        var target = byElement.get(entries[i].target);
        if (target) target.inBand = entries[i].isIntersecting;
      }

      reconcile(timestamp);
    }

    // Hands the slot to the section under the line; an empty band keeps the incumbent.
    function reconcile(timestamp) {
      if (suspended) {
        if (active) {
          emit(active, 'exit', timestamp);
          active = null;
        }
        return;
      }

      var next = pickCandidate();
      if (!next || next === active) return;

      if (active) {
        emit(active, 'exit', timestamp);
        active = null;
      }

      active = next;
      emit(next, 'entry', timestamp);
    }

    // Sole occupant of the band, or the best of several.
    function pickCandidate() {
      var only = null;
      var count = 0;

      for (var i = 0; i < targets.length; i++) {
        if (!targets[i].inBand) continue;
        count += 1;
        if (!only) only = targets[i];
      }

      if (count < 2) return only;
      return rankInBand();
    }

    // Most of the band covered, then nearest the line, then smaller position.
    function rankInBand() {
      var band = bandBounds();
      var best = null;
      var bestCoverage = -1;
      var bestDistance = Infinity;

      for (var i = 0; i < targets.length; i++) {
        var target = targets[i];
        if (!target.inBand) continue;

        var rect = target.element.getBoundingClientRect();
        var coverage = Math.max(
          0,
          Math.min(rect.bottom, band.bottom) - Math.max(rect.top, band.top)
        );
        var distance = 0;
        if (rect.top > band.line) distance = rect.top - band.line;
        else if (rect.bottom < band.line) distance = band.line - rect.bottom;

        var wins =
          !best ||
          coverage > bestCoverage + RANK_EPSILON_PX ||
          (coverage >= bestCoverage - RANK_EPSILON_PX && distance < bestDistance - RANK_EPSILON_PX);

        if (wins) {
          best = target;
          bestCoverage = coverage;
          bestDistance = distance;
        }
      }

      return best;
    }

    // The reading band in viewport pixels.
    function bandBounds() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      var line = viewportHeight * READING_LINE;
      var half = (viewportHeight * BAND_HEIGHT) / 2;
      return { top: line - half, bottom: line + half, line: line };
    }

    // Sends one section event, rejecting anything that breaks alternation.
    function emit(target, type, timestamp) {
      var expected = lastEmitted === 'entry' ? 'exit' : 'entry';
      if (type !== expected) return;

      lastEmitted = type;
      post(worker, 'section-event', {
        sectionId: target.id,
        sectionKey: target.key,
        event: type,
        timestamp: timestamp
      });
      log(type === 'entry' ? 'section_entry' : 'section_exit', target.id);
    }

    // Suspends tracking while the tab is hidden.
    function handleVisibilityChange() {
      suspended = document.visibilityState === 'hidden';
      reconcile(Date.now());
    }

    // Closes the open section and flushes both log buffers.
    function handlePageHide() {
      suspended = true;
      reconcile(Date.now());
      flushLogs();
      worker.postMessage({
        type: 'flush-logs',
        debug: isDebug(),
        serverInfo: isServerInfoEnabled()
      });
    }

    // DOM id of a tracked section, looked up by its config key.
    function sectionId(key) {
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].key === key) return targets[i].id;
      }
      return null;
    }

    // Restates the current section after a cooldown, without breaking alternation.
    function resync() {
      if (!active || suspended) return;

      post(worker, 'section-event', {
        sectionId: active.id,
        sectionKey: active.key,
        event: 'entry',
        timestamp: Date.now(),
        resync: true
      });
      log('section_entry_resync', active.id);
    }

    observe();
    document.addEventListener(
      'visibilitychange',
      guard('error_visibility_change', handleVisibilityChange)
    );
    window.addEventListener('pagehide', guard('error_page_hide', handlePageHide));

    return {
      targets: targets.map(function (target) {
        return { id: target.id, key: target.key };
      }),
      resync: resync,
      sectionId: sectionId
    };
  }

  // =========================================================================
  //  BOOT
  // =========================================================================

  // True only at the breakpoint where the mobile PDP layout applies.
  function isMobileViewport() {
    try {
      if (typeof window.matchMedia === 'function') {
        return window.matchMedia('(max-width: ' + MOBILE_MAX_WIDTH + 'px)').matches;
      }
      return (window.innerWidth || 0) <= MOBILE_MAX_WIDTH;
    } catch (error) {
      warn('error_mobile_check', error);
      return false;
    }
  }

  // Reads config, starts the worker and begins tracking.
  function start() {
    if (!abFlag(AB_ENABLE_FLAG)) return;
    if (!isMobileViewport()) return;

    var config = readConfig();
    if (!config) return;

    logDeviceId = deviceId();

    if (!config.sections || !config.sections.length) {
      warn('sections_unconfigured', 'no section ids in theme settings');
      return;
    }

    if (!('IntersectionObserver' in window) || typeof window.Worker !== 'function') {
      warn('unsupported_browser', 'IntersectionObserver or Worker unavailable');
      return;
    }

    var worker = createWorker(config.workerUrl);
    if (!worker) return;

    var tracker = null;

    worker.addEventListener(
      'message',
      guard('error_worker_message', function (event) {
        var message = event.data || {};
        window.__predictiveBot = { worker: worker, lastMessage: message };

        switch (message.type) {
          case 'ready':
            watchDataLayer(worker, message.payload && message.payload.cooldownEvents);
            break;
          case 'nudge':
            dispatchNudge(message.payload, tracker);
            break;
          case 'request-resync':
            if (tracker) tracker.resync();
            break;
          default:
            break;
        }
      })
    );

    worker.addEventListener('error', function (error) {
      warn('worker_error', error.message || error, error.filename || '');
    });

    tracker = createTracker(worker, config);
    if (!tracker) {
      warn('sections_absent', 'no configured section id is in the DOM');
      worker.terminate();
      return;
    }

    post(worker, 'init', {
      sections: config.sections,
      shopConfig: config.shopConfig,
      context: {
        url: window.location.href,
        template: config.template || null,
        productId: config.productId || null,
        observed: tracker.targets,
        deviceId: logDeviceId
      }
    });
  }

  var boot = guard('error_boot', start);

  if (document.readyState === 'complete') {
    onIdle(boot);
  } else {
    window.addEventListener('load', function () {
      onIdle(boot);
    });
  }
})();
